import { TourAnchor } from "training/anchors";
import {
  orderIsSent,
  showsPlanCard,
  textsPatient,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

import { ESTIMATE_ACTIONS_MENU } from "./shared";

/**
 * CLE-3015 Module 5: Payment plans and refunds. Text is John's (review 1, 2026-10-23) where he gave wording;
 * the rest is the curriculum's (curriculum-v3, 2026-10-21). Nothing here cancels, stops outreach or resends:
 * those are `look` steps; the two real clicks (the plan pencil, Send Custom Text) are each followed by a click
 * on Cancel.
 *
 * Review 1: an intro card opens the module and carries the two general notes that were its last cards (plan
 * mistakes, cancelling a paid estimate; 5.10 and 5.11 are gone).
 *
 * 5.2, 5.3 and 5.5 are pictures in the curriculum (the controls exist only after a payment). There are no
 * screenshots yet, so they ship as cards; the pictures are set as `picture` once captured.
 *
 * The $0 branch: on a No Balance Due estimate the plan card's pencil is hidden and the curriculum skips 5.1,
 * so 5.1–5.3 drop out together (`showsPlanCard`), as they do on a hospital without plans.
 */
export const MODULE_5: ITourStep[] = [
  {
    // Review 1. The module's purpose (the popover's intro) carries John's first sentence.
    id: "m5.00-intro-card",
    module: 5,
    mode: "card",
    route: "estimate",
    text: [
      "For this module, we will jump back into the estimate you created, and walk through how to edit the payment plan, refund the patient, and more. Some general notes:",
      "• Payment plans are the most common place for mistakes. Check the number of payments and the down payment before you send, and read the plan back to the patient.",
      "• Cancelling a paid estimate refunds every payment on it and turns the plan off. Partial refunds go to the card that paid.",
    ].join("\n"),
  },
  {
    id: "m5.01-payment-plan-pencil",
    module: 5,
    anchor: TourAnchor.PaymentPlanEdit,
    mode: "open-cancel",
    route: "estimate",
    when: showsPlanCard,
    text: "At the bottom of your estimate will be the payment plan section. Click this pencil to edit the plan.",
    followUp: {
      anchor: TourAnchor.PaymentPlanEditCancel,
      label: "Cancel",
      text: "In this section you can update the number of payments, the down payment, and the frequency of the payments as well as switch the plan off entirely. This is useful, for example, if the insurance pays a different amount than expected and the payment plan needs to be altered. For now, just hit Cancel.",
    },
  },
  {
    // A picture in the curriculum; a card until the screenshots exist.
    // A picture of this will be added here (John, review 1: without one the trainee cannot tell what it means).
    id: "m5.02-plan-after-payment",
    module: 5,
    mode: "card",
    route: "estimate",
    when: showsPlanCard,
    text: 'Once the patient has paid a first installment, "Remaining Amount" opens up for when insurance pays and the patient owes less, and "Next Installment Date" for moving their payment day. Lower the remaining amount and the plan gets shorter; the monthly payment stays the same.',
  },
  {
    // A picture in the curriculum; a card until the screenshots exist.
    // A picture of this will be added here (John, review 1).
    id: "m5.03-per-provider",
    module: 5,
    mode: "card",
    route: "estimate",
    when: showsPlanCard,
    text: "If the plan covers the facility, a physician and anesthesia, each one's remaining balance is listed here so you can adjust them separately as insurance pays.",
  },
  {
    id: "m5.04-cancel-estimate",
    module: 5,
    anchor: TourAnchor.CancelEstimate,
    label: "Cancel Estimate",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    text: "Cancel when the visit is not happening or the estimate was a mistake. It asks you to confirm and lets you leave a note. Texts stop.",
  },
  {
    // A picture in the curriculum; a card until the screenshots exist.
    // A picture of this will be added here (John, review 1).
    id: "m5.05-cancel-and-refund",
    module: 5,
    mode: "card",
    route: "estimate",
    text: [
      "Once a patient has paid, the Collect Payment button disappears and is replaced with two new options - Cancel and Refund & Partial Refund.",
      "Cancel and refund: Cancels the estimate and will refund the paid funds in full back to the patient's card.",
      'Partial Refund: This will open the "Money Transfers" dialog, where you choose the amount and the card it goes back to. Both leave a line in the history with your name. More training will be provided to those doing refunds.',
    ].join("\n"),
  },
  {
    id: "m5.06-stop-outreach",
    module: 5,
    anchor: TourAnchor.StopOutreach,
    label: "Stop outreach",
    mode: "look",
    route: "estimate",
    text: 'A patient says stop texting me. One click, no confirmation, and the label flips to "Enable outreach" so you can turn it back on. Reminders stop; you can still send a custom text by hand.',
  },
  {
    id: "m5.07-custom-text",
    module: 5,
    anchor: TourAnchor.SendCustomText,
    label: "Send Custom Text",
    mode: "open-cancel",
    route: "estimate",
    when: textsPatient,
    // The dialog's hint shows while the dialog is open.
    text: "Your own words to this one patient, for example to say the time changed.",
    followUp: {
      anchor: TourAnchor.CustomTextCancel,
      label: "Cancel",
      text: [
        "In this screen, you can input a custom message directly to the patient's cell phone. To include the payment link, type exactly {{url}} in the message where you want their payment link.",
        "For now, hit Cancel to move to the next task.",
      ].join("\n"),
    },
  },
  {
    // Shown only on a Sent estimate (PatientDetails.tsx).
    id: "m5.08-resend",
    module: 5,
    anchor: TourAnchor.ResendNotification,
    label: "Resend Notification",
    mode: "look",
    route: "estimate",
    when: orderIsSent,
    text: "Sends the original estimate text again, so clicking it now would text you once more. Use it when a patient says they never got it.",
  },
  {
    // John (review 1): keep for now; he revisits it once the module screens are in.
    id: "m5.09-reminders-card",
    module: 5,
    mode: "card",
    route: "estimate",
    text: "After the first text, the patient gets up to two reminder texts before the visit, at times your hospital set with Clear Health, and Clear Health support may call. Stop outreach ends all of these. Plan installments are charged automatically at 1 PM Central on the due day, with a reminder the day before.",
  },
];
